/**
 * QuizStyleProvider — selects which visual variant the quiz components render.
 *
 * - 'default': current cross-app look (blue-600 CTA, generic Tailwind tokens).
 *              B2C / Company use this implicitly by not mounting the provider.
 * - 'lc':      LC Learner Design System (3D orange CTA, lc-* design tokens).
 *              LC app wraps its QuizRunner / PauseSolvePanel with <QuizStyleProvider variant="lc">.
 * - 'aiq':     AIQ 진단(LC v3 key-blue/navy 토큰).
 *              AIQ 출제 화면을 <QuizStyleProvider variant="aiq">로 감싼다.
 */
import { createContext, useContext, type ReactNode } from 'react'

export type QuizStyleVariant = 'default' | 'lc' | 'aiq'

const QuizStyleContext = createContext<QuizStyleVariant>('default')
const QuizHeaderSuffixContext = createContext<ReactNode>(null)

interface QuizStyleProviderProps {
  variant: QuizStyleVariant
  children: ReactNode
  headerSuffix?: ReactNode
}

export function QuizStyleProvider({ variant, children, headerSuffix }: QuizStyleProviderProps) {
  return (
    <QuizStyleContext.Provider value={variant}>
      <QuizHeaderSuffixContext.Provider value={headerSuffix ?? null}>
        {children}
      </QuizHeaderSuffixContext.Provider>
    </QuizStyleContext.Provider>
  )
}

export function useQuizStyleVariant(): QuizStyleVariant {
  return useContext(QuizStyleContext)
}

export function useQuizHeaderSuffix(): ReactNode {
  return useContext(QuizHeaderSuffixContext)
}
